import { useState } from "react"
import { publicPost } from "../api/client"
import type { AuthAdapter, AuthUser } from "./types"

/** Dev-only sign-in: the server mints tokens at POST /api/dev/token on loopback. */

const KEY = "walkie.dev-session"

interface DevSession {
	token: string
	user: AuthUser
}

function read(): DevSession | null {
	try {
		const raw = sessionStorage.getItem(KEY)
		return raw ? (JSON.parse(raw) as DevSession) : null
	} catch {
		return null
	}
}

const PEOPLE = [
	{ sub: "dev-ada", email: "ada@example.dev", name: "Ada" },
	{ sub: "dev-grace", email: "grace@example.dev", name: "Grace" },
]

function DevSignIn({ onSignedIn }: { onSignedIn: (u: AuthUser) => void }) {
	const [busy, setBusy] = useState<string | null>(null)
	const [err, setErr] = useState<string | null>(null)
	const go = async (p: (typeof PEOPLE)[number]) => {
		setBusy(p.sub)
		setErr(null)
		try {
			const { token } = await publicPost<{ token: string }>("/api/dev/token", p)
			const user = { id: p.sub, email: p.email, name: p.name }
			sessionStorage.setItem(KEY, JSON.stringify({ token, user } satisfies DevSession))
			onSignedIn(user)
		} catch (e) {
			setErr((e as Error).message)
		} finally {
			setBusy(null)
		}
	}
	return (
		<div className="signin-buttons">
			{PEOPLE.map(p => (
				<button
					key={p.sub}
					className="btn btn-primary"
					disabled={!!busy}
					onClick={() => go(p)}
				>
					{busy === p.sub ? "Signing in…" : `Sign in as ${p.name} (dev)`}
				</button>
			))}
			{err && (
				<div className="form-error" role="alert">
					{err}
				</div>
			)}
		</div>
	)
}

export function createDevAuth(): AuthAdapter {
	return {
		kind: "dev",
		restore: async () => read()?.user ?? null,
		getToken: async () => {
			const s = read()
			if (!s) throw new Error("Not signed in")
			return s.token
		},
		signOut: async () => sessionStorage.removeItem(KEY),
		SignIn: DevSignIn,
	}
}
